/**
 * What the drivers' phones may do.
 *
 * Nine switches and one number, saved as one set like the dispatch settings. Each switch
 * is the firm's decision rather than ours, and each one is a permission the server
 * enforces rather than a button the app hides — the app is on somebody else's phone.
 *
 * Grouped by what the switch changes: the two about talking to the office, the two that
 * change who is offered what, the four that change what the phone shows, and the one that
 * lets drivers place the firm's entrances at the door (#420).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { driverAppLimits, settingsApi, type DriverAppSettings } from '../api';
import { checkForm, named } from '../check';
import { refusal } from '../errors';
import {
  Banner,
  EmptyState,
  FieldErrors,
  Measured,
  PageHeader,
  SaveBar,
  Section,
  SettingRow,
  Switch,
  type FieldProblem,
} from '../../ui';

type Toggle = Exclude<keyof DriverAppSettings, 'nextJobMinutesBeforeFree'>;

export function DriverAppPage() {
  const { t } = useTranslation();
  // What `checkForm` reads before a save: every control on the page, at once.
  const page = useRef<HTMLDivElement>(null);

  const [loaded, setLoaded] = useState<DriverAppSettings | null>(null);
  const [draft, setDraft] = useState<DriverAppSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<FieldProblem | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const settings = await settingsApi.driverApp();

        setLoaded(settings);
        setDraft(settings);
      } catch (failure) {
        setError(refusal(failure, t, 'settings.couldNotLoad'));
      }
    })();
  }, [t]);

  // The confirmation is a moment, not a state; see DispatchPage.
  useEffect(() => {
    if (!saved) return;

    const timer = globalThis.setTimeout(() => setSaved(false), 4000);

    return () => globalThis.clearTimeout(timer);
  }, [saved]);

  const change = useCallback(
    <K extends keyof DriverAppSettings>(key: K, value: DriverAppSettings[K]) => {
      setSaved(false);
      setDraft((current) => (current === null ? current : { ...current, [key]: value }));
    },
    [],
  );

  const changes = useMemo(() => {
    if (loaded === null || draft === null) return 0;

    return (Object.keys(loaded) as (keyof DriverAppSettings)[]).filter(
      (key) => loaded[key] !== draft[key],
    ).length;
  }, [loaded, draft]);

  async function save() {
    if (draft === null) return;

    const invalid = checkForm(page.current, t);

    if (invalid !== null) {
      setError(invalid);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const stored = await settingsApi.saveDriverApp(draft);

      setLoaded(stored);
      setDraft(stored);
      setSaved(true);
    } catch (failure) {
      setError(named(refusal(failure, t, 'settings.couldNotSave'), page.current, t));
    } finally {
      setSaving(false);
    }
  }

  const toggle = (key: Toggle, last = false) =>
    draft === null ? null : (
      <SettingRow label={t(`driverApp.${key}`)} help={t(`driverApp.${key}Help`)} last={last}>
        <Switch
          checked={draft[key]}
          label={t(`driverApp.${key}`)}
          onChange={(next) => change(key, next)}
        />
      </SettingRow>
    );

  return (
    <FieldErrors root={page} problem={error} onEdit={() => setError(null)}>
      <PageHeader
        title={t('nav.settings.driverApp')}
        description={t('nav.settingsAbout.driverApp')}
      />

      {error !== null && <Banner tone="danger">{error.message}</Banner>}
      {saved && <Banner tone="ok">{t('settings.saved')}</Banner>}

      {draft === null ? (
        error === null && <EmptyState>{t('app.loading')}</EmptyState>
      ) : (
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Section title={t('driverApp.groupTalk')} description={t('driverApp.groupTalkHelp')}>
            {toggle('messagingEnabled')}
            {toggle('alarmEnabled', true)}
          </Section>

          <Section title={t('driverApp.groupOffers')} description={t('driverApp.groupOffersHelp')}>
            {toggle('headingHomeEnabled')}
            {toggle('nextJobEnabled', !draft.nextJobEnabled)}

            {/* The number only means something while the switch above it is on. */}
            {draft.nextJobEnabled && (
              <SettingRow
                label={t('driverApp.nextJobMinutesBeforeFree')}
                help={t('driverApp.nextJobMinutesBeforeFreeHelp')}
                last
              >
                <Measured
                  id="nextJobMinutesBeforeFree"
                  unit={t('settings.unitMinutes')}
                  type="number"
                  inputMode="numeric"
                  aria-label={t('driverApp.nextJobMinutesBeforeFree')}
                  required
                  value={Number.isNaN(draft.nextJobMinutesBeforeFree) ? '' : draft.nextJobMinutesBeforeFree}
                  min={driverAppLimits.nextJobMinutesBeforeFree.min}
                  max={driverAppLimits.nextJobMinutesBeforeFree.max}
                  onChange={(e) => change('nextJobMinutesBeforeFree', e.target.valueAsNumber)}
                />
              </SettingRow>
            )}
          </Section>

          <Section title={t('driverApp.groupStreet')} description={t('driverApp.groupStreetHelp')}>
            {toggle('streetRidesEnabled', true)}
          </Section>

          <Section title={t('driverApp.groupShows')} description={t('driverApp.groupShowsHelp')}>
            {toggle('heatmapEnabled')}
            {toggle('ratePassengersEnabled')}
            {toggle('showFares')}
            {toggle('taximeterEnabled', true)}
          </Section>

          <Section title={t('driverApp.groupPlaces')} description={t('driverApp.groupPlacesHelp')}>
            {toggle('rememberEntrancesEnabled', true)}
          </Section>

          <Section title={t('driverApp.groupDevices')} description={t('driverApp.groupDevicesHelp')}>
            {toggle('serviceDeviceRequestsEnabled', true)}
          </Section>

          <SaveBar
            changes={changes}
            problem={error?.message}
            busy={saving}
            discardLabel={t('settings.discard')}
            saveLabel={saving ? t('settings.saving') : t('settings.save')}
            onDiscard={() => {
              setSaved(false);
              setError(null);
              setDraft(loaded);
            }}
            onSave={() => void save()}
          >
            {t('settings.unsaved', { count: changes })}
          </SaveBar>
        </form>
      )}
    </FieldErrors>
  );
}
